import React, {useCallback} from 'react';
import {Venue} from '../../interfaces';
import {Box, Button, Modal, Typography} from '@mui/material';
import {Control, FieldValues, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import styles from '../../styles';
import FormTextField from '../shared/FormTextField';
import {updateVenue} from '../../api/adminAPI';

type VenueUpdateForm = {
  id: number;
  name: string;
  country: string;
  stateOrProvince: string;
  address: string;
  city: string;
  lat: string;
  lon: string;
};

interface Props {
  venue: Venue;
  loading: boolean;
  onUpdate: () => void;
}

const UpdateVenueModal: React.FC<Props> = ({venue, loading, onUpdate}) => {
  const {t} = useTranslation();
  const [showUpdateDialog, setShowUpdateDialog] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const {handleSubmit, reset, control} = useForm<VenueUpdateForm>({
    defaultValues: {
      id: venue.id,
      name: venue.name || '',
      country: venue.country || '',
      stateOrProvince: venue.stateOrProvince || '',
      address: venue.address || '',
      city: venue.city || '',
      lat: venue.lat?.toString() || '',
      lon: venue.lon?.toString() || '',
    },
  });

  const handleUpdateDialogClick = useCallback(() => {
    setShowUpdateDialog(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    reset();
    setPending(false);
    setShowUpdateDialog(false);
  }, [reset]);

  const handleUpdateSubmit = useCallback(
    async (data: VenueUpdateForm) => {
      setPending(true);
      await updateVenue({
        id: data.id,
        name: data.name,
        country: data.country,
        stateOrProvince: data.stateOrProvince,
        address: data.address || undefined,
        city: data.city || undefined,
        lat: data.lat ? parseFloat(data.lat) : undefined,
        lon: data.lon ? parseFloat(data.lon) : undefined,
      });
      onUpdate();
      handleDialogClose();
    },
    [handleDialogClose, onUpdate],
  );

  return (
    <>
      <Button
        sx={{mb: 2, mr: 2}}
        onClick={handleUpdateDialogClick}
        disabled={loading}>
        {t('admin.updateVenue')}
      </Button>
      <Modal
        open={showUpdateDialog}
        onClose={handleDialogClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={styles.modal}>
          <Typography variant="h4" sx={{mb: 2}}>
            {t('admin.updateVenue')}
          </Typography>
          <form onSubmit={handleSubmit(handleUpdateSubmit)} className="form">
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="name"
              label={t('general.name') as string}
              required
            />
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="country"
              label={t('admin.country') as string}
              required
            />
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="stateOrProvince"
              label={t('admin.stateOrProvince') as string}
              required
            />
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="city"
              label={t('admin.city') as string}
            />
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="address"
              label={t('admin.address') as string}
            />
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="lat"
              label={t('admin.latitude') as string}
            />
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="lon"
              label={t('admin.longitude') as string}
            />
            <Button disabled={pending} type="submit">
              {t('general.submit')}
            </Button>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default React.memo(UpdateVenueModal);
